import type { BuilderContent } from '@builder.io/react'
import type { BuilderContent as BuilderContentSDK, Input } from '@builder.io/sdk'
import camelCase from 'lodash/camelCase'
import pipe from 'lodash/fp/pipe'

import { configPublic } from '@src/config.public'
import type { LocaleCode } from '@src/i18n/locales'
import { getIsServer } from '@src/utils/common.util'
import { transformExternalBODiLinkInText } from '@src/utils/externalBodiLink'
import { removeNestedLinkElements } from '@src/utils/fixNestedBuilderLinks'
import { proxyImages } from '@src/utils/imageProxy.util'

export const getUserAttributes = ({
  urlPath,
  locale,
  searchParams,
}: {
  urlPath: string
  locale: string
  searchParams: any
}) => {
  const params = new URLSearchParams()
  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v))
      } else if (value !== undefined) {
        params.append(key, String(value))
      }
    })
  }
  return Object.entries(searchParams).reduce(
    (str, [p, val]) => {
      // ignore builder built-in parameters
      if (p.includes('builder')) {
        return str
      }
      return { ...str, [p]: `'${val}'` }
    },
    {
      urlPath,
      locale,
      queryString: params.toString(),
      shopifyEnabled: configPublic.featureFlags.shopifyEnabled,
    }
  )
}

export interface BuilderPreviewMode {
  edit: boolean
  live: boolean
  draft: boolean
}

const emptyBuildertState = {
  edit: false,
  live: false,
  draft: false,
}

const builderPreviewState = {
  edit: {
    ...emptyBuildertState,
    edit: true,
  },
  live: {
    ...emptyBuildertState,
    live: true,
  },
  draft: {
    ...emptyBuildertState,
    draft: true,
  },
}

/**
 * Isomorphic function to check if the current context is in preview mode
 *
 * @param searchParams (optional) - searchParams object if on server-side
 * @returns
 * ```
 * {
 *  edit: boolean
 *  live: boolean
 *  draft: boolean
 * }
 * ```
 */
export const getBuilderPreviewMode = (
  searchParams?: Record<string, any> | URLSearchParams | null
): BuilderPreviewMode => {
  const params = getIsServer()
    ? new URLSearchParams(searchParams ?? undefined)
    : new URLSearchParams(window.location.search)
  if (params.has('builder.frameEditing')) return builderPreviewState.edit
  else if (params.has('builder.preview')) return builderPreviewState.draft
  return builderPreviewState.live
}

export const filterOutBuilderProps = <T extends object>(props: T) => {
  const {
    builderState,
    builderBlock,
    actionName,
    addItemsToCart,
    upsellToCart,
    revertUpsell,
    addPDPItemToCart,
    navigate,
    popoutVideo,
    openModal,
    selectSKU,
    addSelectedSKUsToCart,
    loginModal,
    ...rest
  } = props as any

  return rest as Exclude<
    T,
    | 'builderState'
    | 'builderBlock'
    | 'actionName'
    | 'addItemsToCart'
    | 'upellToCart'
    | 'revertUpsell'
    | 'addPDPItemToCart'
    | 'navigate'
    | 'popoutVideo'
    | 'openModal'
    | 'selectSKU'
    | 'addSelectedSKUsToCart'
    | 'loginModal'
  >
}

/** Returns only the Builder attributes needed to style and manage a custom component. */
export const filterBuilderAttributes = (attributes: any = {}) => {
  return Object.entries(attributes).reduce((acc, [key, value]) => {
    if (['className', 'style', 'builder-id', 'data-testid'].includes(key))
      return { ...acc, [key]: value }
    return acc
  }, {}) as Record<'className' | 'style' | 'builder-id' | 'data-testid', any>
}

export type Spacing = 'standard' | 'none' | undefined

export const spacingBuilderInputs = {
  innerSpacing: {
    name: 'innerSpacing',
    type: 'text',
    enum: ['standard', 'none'],
    defaultValue: 'none',
  },
  outerSpacing: {
    name: 'outerSpacing',
    type: 'text',
    enum: ['standard', 'none'],
    defaultValue: 'none',
  },
}

export const getInnerSpacingClassNames = (spacing: Spacing) => {
  const innerSpacingConst: Record<string, string | undefined> = {
    standard: 'lg:py-20 py-12',
    none: 'py-0',
  }

  return innerSpacingConst[spacing as string] ?? ''
}

export const getOuterSpacingClassNames = (spacing: Spacing) => {
  const outerSpacingConst: Record<string, string | undefined> = {
    standard: 'lg:mb-20 mb-12',
    none: 'mb-0',
  }
  return outerSpacingConst[spacing as string] ?? ''
}

/**
 * This factory function returns a function that can be used to handle image changes in Builder.io.
 * When an image is changed, it generates a data URI for the new image and sets it to the specified property.
 *
 * The function is defined in a string and then converted to a function using `new Function`, this is because
 * Builder.io does not support functions with closures.
 **/
export function getOnChangeImageHandler(args: { srcProperty: string; dataUriProperty: string }) {
  const fn = `
  async function handler(options) {
    const srcImage = options?.get('${args.srcProperty}')
    const dataUrl = await generataDataURI(srcImage)
    options.set('${args.dataUriProperty}', dataUrl)
  }

  async function generataDataURI(url) {
    return await new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const MAX_WIDTH = 64
        const MAX_HEIGHT = 64
        const wRatio = MAX_WIDTH / img.width
        const hRatio = MAX_HEIGHT / img.height
        let width, height
        if (wRatio > hRatio) {
          width = MAX_WIDTH
          height = wRatio * img.height
        } else {
          width = hRatio * img.width
          height = MAX_HEIGHT
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        ctx.filter = 'blur(1px)'
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.25))
      }
      img.onerror = (err) => {
        resolve('')
      }
      img.src = url
    })
  }

  return handler(options)
  `
  return new Function(fn) as unknown as Input['onChange']
}

const showIfContent: any = {
  locale: (properties: any) => properties.get('visibilityBasedOn') === 'locale',
  experiment: (properties: any) => properties.get('visibilityBasedOn') === 'experiment',
  queryParams: (properties: any) => properties.get('visibilityBasedOn') === 'queryParams',
  loggedIn: (properties: any) => properties.get('visibilityBasedOn') === 'loggedIn',
  hasBodiAccess: (properties: any) => properties.get('visibilityBasedOn') === 'hasBodiAccess',
  sku: (properties: any) => properties.get('visibilityBasedOn') === 'sku',
  shopifyEnabled: (properties: any) => properties.get('visibilityBasedOn') === 'shopifyEnabled',
}

export type visibilityOptions = any

export const visibilityInputs = (visibilityOptions: visibilityOptions) => {
  const criteria = Object.keys(visibilityOptions)

  const criteriaInput = {
    name: 'visibilityBasedOn',
    friendlyName: 'Visibility based on',
    type: 'string',
    enum: criteria,
  }

  return [
    criteriaInput,
    ...criteria.reduce((acc: visibilityOptions, criterion) => {
      let optionInputs = []
      if (criterion === 'experiment') {
        optionInputs = [
          {
            name: 'experimentFlag',
            friendlyName: `Experiment Flag`,
            type: 'string',
            showIf: showIfContent[criterion],
          },
          {
            name: 'experimentVariant',
            friendlyName: `Experiment Variant`,
            type: 'string',
            showIf: showIfContent[criterion],
          },
          {
            name: 'showAsOff',
            friendlyName: 'Show Contents to users not in experiment?',
            type: 'boolean',
            defaultValue: false,
            helperText:
              'If the experiment is not rolled out, but you still want this content to show by default, i.e OFF in amplitude, then set to true, and label variant as control. This will make content show up for users in OFF and Control, which is typically the same experience.',
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'queryParams') {
        optionInputs = [
          {
            name: 'matchType',
            friendlyName: 'Match Type',
            type: 'string',
            defaultValue: 'contains',
            enum: ['contains', 'does not contain', 'regEx'],
            showIf: showIfContent[criterion],
          },
          {
            name: 'queryParams',
            friendlyName: 'Query Parameters',
            type: 'string',
            helperText:
              "Use regEx for more complex queries, e.g. 'utm_source=google|utm_medium=cpc'. Enter regEx as just next and omit the / /.",
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'loggedIn') {
        optionInputs = [
          {
            name: 'showIfLoggedIn',
            friendlyName: 'Show if logged in?',
            type: 'boolean',
            defaultValue: true,
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'hasBodiAccess') {
        optionInputs = [
          {
            name: 'showIfHasBodiAccess',
            friendlyName: 'Show if has BODi access?',
            type: 'boolean',
            defaultValue: true,
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'sku') {
        optionInputs = [
          {
            name: 'skusInOut',
            type: 'boolean',
            friendlyName: 'Is SKU In Cart?',
            defaultValue: false,
            helperText:
              'If true, content will only show if the SKU(s) are in the cart. If false, content will only show if SKU(s) are NOT in cart.',
            showIf: showIfContent[criterion],
          },
          {
            name: 'anyOrAll',
            type: 'string',
            friendlyName: 'Any SKU or All SKUS',
            enum: ['Any', 'All'],
            defaultValue: 'Any',
            helperText:
              'Any means if any of the SKUs are in the cart, All means all of the SKUs must be in the cart',
            showIf: showIfContent[criterion],
          },
          {
            name: 'skus',
            friendlyName: 'SKUs',
            type: 'Tags',
            helperText:
              'Enter sku and press enter. If you want to use multiple SKUs, enter them one by one.',
            defaultValue: [],
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'shopifyEnabled') {
        optionInputs = [
          {
            name: 'showIfShopifyEnabled',
            friendlyName: 'Show if Shopify is enabled?',
            type: 'boolean',
            defaultValue: true,
            showIf: showIfContent[criterion],
          },
        ]
      } else if (criterion === 'locale') {
        optionInputs = visibilityOptions[criterion].map((inputCriteria: any) => {
          return {
            name: `${criterion}_${inputCriteria}`,
            friendlyName: `hide on ${inputCriteria}`,
            type: 'boolean',
            showIf: showIfContent[criterion],
          }
        })
      }
      return [...acc, ...optionInputs]
    }, []),
  ]
}

/** @example { '@type': 'LocalizedValue', Default: 'Default value', 'en-US': 'English value', 'fr-CA': 'French value' } */
export type LocalizedValue = Partial<
  {
    '@type': string
    Default: string
  } & Record<LocaleCode, string>
>

export const getBuilderLocalizedValue = ({
  value,
  locale,
  noDefault = false,
}: {
  value: LocalizedValue
  locale: string
  noDefault?: boolean
}) => {
  if (value?.[locale as keyof LocalizedValue]) {
    return value[locale as keyof LocalizedValue] ?? ''
  }
  return noDefault ? '' : value.Default
}

export const processExternalBODiLinks = (data: BuilderContent | BuilderContentSDK) => {
  function replacer(_key: string, value: any) {
    if (typeof value === 'string') {
      return transformExternalBODiLinkInText(value)
    }
    return value
  }
  return JSON.parse(JSON.stringify(data, replacer))
}

/**
 * Removes the `_newProperty` binding from Builder content.
 * Fixes "React does not recognize the `_newProperty` prop on a DOM element."
 *
 * @param content - The Builder content to process
 * @returns A new Builder content object with `_newProperty` bindings removed
 */
export const removeBuilderBindingProps = (content: BuilderContent | BuilderContentSDK) => {
  function replacer(key: string, value: any) {
    if (key === 'bindings') {
      delete value['_newProperty']
    }
    return value
  }
  return JSON.parse(JSON.stringify(content, replacer))
}

/**
 * Processes the `responsiveStyles` property of Builder content.
 * Converts the `responsiveStyles` object to a new object with camelCase keys.
 *
 * @param content - The Builder content to process
 * @returns A new Builder content object with `responsiveStyles` processed
 */
export function processStyleProps(content: BuilderContent | BuilderContentSDK) {
  function replacer(key: string, value: any) {
    if (key === 'responsiveStyles') {
      for (const breakpoint in value) {
        for (const style in value[breakpoint]) {
          const content = value[breakpoint][style]
          delete value[breakpoint][style]
          value[breakpoint][camelCase(style)] = content // ensure each style key is camelCase
        }
      }
    }
    return value
  }
  return JSON.parse(JSON.stringify(content, replacer))
}

/**
 * Processes the custom code of Builder content.
 * Ensures that <script> tags are rendered server-side.
 *
 * @param content - The Builder content to process
 * @returns A new Builder content object with `component` processed
 */
export function processCustomCode(content: BuilderContent | BuilderContentSDK) {
  function replacer(key: string, value: any) {
    if (key === 'component' && value.options?.code?.includes('<script>')) {
      value.options.scriptsClientOnly = false
    }
    return value
  }
  return JSON.parse(JSON.stringify(content, replacer))
}

export function processBuilderContent(
  content: BuilderContent | BuilderContentSDK | undefined,
  builderMode?: BuilderPreviewMode
) {
  if (!content) return undefined
  if (builderMode?.edit) return undefined // conflicts with Builder.io editor overrides
  const processedContent = pipe(
    proxyImages,
    processExternalBODiLinks,
    processStyleProps,
    processCustomCode,
    removeBuilderBindingProps,
    removeNestedLinkElements
  )(content)
  return processedContent
}
